import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Icons, Tooltip, Typography } from '@usepepper/sprout-kit-ui';
import { View } from 'react-native';

interface OrderEntryOverStockWarningProps {
  /** Number of units the cart quantity exceeds on-hand availability. */
  overBy: number;
}

/**
 * Displays an orange warning icon in the On Hand column when the requested
 * quantity exceeds on-hand availability. Hovering the icon shows a tooltip
 * with the over-by amount.
 *
 * @param props - {@link OrderEntryOverStockWarningProps}
 */
export const OrderEntryOverStockWarning: React.FC<
  OrderEntryOverStockWarningProps
> = ({ overBy }) => {
  const [styles, theme] = useStyles();

  return (
    <Tooltip delay={0} placement="top">
      <Tooltip.Trigger>
        <View
          accessibilityLabel={i18n.t(
            'sales_portal.order_entry.table.over_stock_a11y_label',
            { overBy },
          )}
          accessibilityRole="image"
          style={styles.iconContainer}
        >
          <Icons.AlertCircle color={theme.sk.color.text.warning} size="$16" />
        </View>
      </Tooltip.Trigger>
      <Tooltip.Content
        animation={null}
        backgroundColor="$bgPrimaryDefault"
        borderColor="$borderPrimary"
        borderRadius="$8"
        borderWidth={1}
        paddingHorizontal="$8"
        paddingVertical="$4"
      >
        <Typography size="$sm">
          {i18n.t('sales_portal.order_entry.table.over_stock_tooltip_label')}{' '}
          <Typography bold size="$sm">
            {overBy}
          </Typography>
        </Typography>
      </Tooltip.Content>
    </Tooltip>
  );
};

const useStyles = createStyles((theme) => ({
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.sk.spacing.spaceXsmall,
  },
}));
